import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgxSpinnerService } from 'ngx-spinner';
import { ConfirmationService } from 'primeng/api';
import { NotificationTypes } from 'src/app/app.enums';
import { AuthService, ResponseObject } from 'src/app/Services/auth.service';
import { GalleryConsignmentProduct } from 'src/app/Services/consignment.service';
import { NotificationsService } from 'src/app/Services/shared/notifications.service';
import {
  ShopifyAccountDetails,
  ShopifyService,
  ShopifyShopUrls,
  ShopifyStoreAccount,
} from 'src/app/Services/shopify-service/shopify.service';
import { SyncProductsComponent } from '../sync-products/sync-products.component';
import { TranslocoService } from '@jsverse/transloco';

@Component({
    selector: 'shopify-account',
    templateUrl: './shopify-account.component.html',
    inputs: ['isSentByEditProfile', 'isShopifyConnectionDailogVisible'],
    standalone: false
})
export class ShopifyAccount implements OnInit {
  // Reference to child SyncProductsComponent
  @ViewChild(SyncProductsComponent) syncProductsComponent!: SyncProductsComponent;

  // Component properties
  public shopifyAccount: ShopifyAccountDetails | undefined;
  public shopifyAccounts: ShopifyStoreAccount[] = [];
  public isShopifyCheckoutSuccessDialogVisible!: boolean;
  public shopifySyncProducts: boolean = false;
  public isShopifyConnectionDailogVisible: boolean = false;
  public isAuthenticated: boolean = false;
  public isSentByEditProfile: boolean = false;
  public ShopifyShopUrls!: ShopifyShopUrls;
  public Products: GalleryConsignmentProduct[] = [];
  public status: boolean = false;
  public storeUrl: string = '';

  constructor(
    private ngxSpinnerService: NgxSpinnerService,
    private shopifyService: ShopifyService,
    private notificationsService: NotificationsService,
    private authService: AuthService,
    private confirmationService: ConfirmationService,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private transloco: TranslocoService,
  ) { }

  ngOnInit(): void {
    this.isAuthenticated = this.authService.isAuthenticated();

    // Handle Shopify OAuth callback if URL contains params
    this.activatedRoute.queryParams.subscribe((params) => {
      console.log("Query Param on shopify route ", params);

      if (params['code'] && params['state'] && params['shop'] && params['host']) {
        this.onConnectWithShopifyCallBack(params['code'], params['state'], params['shop'], params['host']);
      } else {
        if (this.isAuthenticated && this.isSentByEditProfile == false) {
          this.getShopifyAccountDetails();
        }
      }
    });

    console.log("this.isShopifyConnectionDailogVisible", this.isShopifyConnectionDailogVisible);
  }

  /**
   * Show Shopify connection dialog
   */
  public onShowShopifyConnectionDailog() {
    this.isShopifyConnectionDailogVisible = true;
    console.log('Connect Shopify button clicked!', this.isShopifyConnectionDailogVisible);
  }

  /**
   * Close Shopify connection dialog
   * @param $event - Boolean indicating whether to close the dialog
   */
  public onCloseShopifyConnectionDailog($event: boolean) {
    this.isShopifyConnectionDailogVisible = $event;
  }

  /**
   * Connect with Shopify account using store URL
   */
  public ConnectWithShopifyAccount(): void {
    this.ngxSpinnerService.show();
    this.shopifyService.connectWithShopifyAccount(this.storeUrl).subscribe(
      (response) => {
        if (response.Success) {
          this.ngxSpinnerService.hide();
          console.log('Shopify account connection successful:', response);
          window.location.href = response.ResponseData; // Redirect to Shopify OAuth
        }
      },
      (error) => {
        this.ngxSpinnerService.hide();
        console.error('Failed to connect to Shopify account:', error);
        this.notificationsService.showNotification(
          this.transloco.translate('shopifyAccount.connectionFailedTitle'),
          this.transloco.translate('shopifyAccount.connectionError'),
          NotificationTypes.Error
        );
      }
    );
  }

  /**
   * Install app from Shopify Partner Dashboard
   */
  public onInstallAppFromShopifyPartnerDashboard(
    hmac: string,
    shop: string,
    host: string,
    timestamp: string
  ): void {
    this.ngxSpinnerService.show();
    this.shopifyService.onInstallAppFromShopifyPartnerDashboard(hmac, shop, host, timestamp).subscribe(
      (response) => {
        if (response.Success) {
          this.ngxSpinnerService.hide();
          console.log('Shopify account connection successful:', response);
          window.location.href = response.ResponseData; // Redirect to Shopify OAuth
        }
      },
      (error) => {
        this.ngxSpinnerService.hide();
        console.error('Failed to connect to Shopify account:', error);
        this.notificationsService.showNotification(
          this.transloco.translate('shopifyAccount.connectionFailedTitle'),
          this.transloco.translate('shopifyAccount.connectionError'),
          NotificationTypes.Error
        );
      }
    );
  }

  /**
   * Handle Shopify OAuth callback
   */
  public onConnectWithShopifyCallBack(code: string, state: string, shop: string, host: string): void {
    this.ngxSpinnerService.show();
    this.shopifyService.onShopifyAccountCallBack(code, state, shop, host).subscribe(
      (res: ResponseObject) => {
        if (res.Success) {
          this.notificationsService.showNotification(this.transloco.translate('shopifyAccount.successTitle'), res.ResponseMessage, NotificationTypes.Success);

          // Clear query params from URL after success
          this.router.navigate([], {
            relativeTo: this.activatedRoute,
            queryParams: {},
            replaceUrl: true,
          });

          this.ngxSpinnerService.hide();
          this.isShopifyCheckoutSuccessDialogVisible = true;
        } else {
          this.ngxSpinnerService.hide();
          this.notificationsService.showNotification(this.transloco.translate('shopifyAccount.errorTitle'), res.ResponseMessage, NotificationTypes.Error);
        }
      },
      (error) => {
        console.error(error);
        this.ngxSpinnerService.hide();
      }
    );
  }

  /**
   * Get Shopify account details for the current user
   */
  public getShopifyAccountDetails() {
    this.ngxSpinnerService.show();
    this.shopifyService.getShopifyAccounts().subscribe(
      (response) => {
        this.ngxSpinnerService.hide();
        if (response) {
          this.shopifyAccounts = response;
          console.log('shopifyAccount', this.shopifyAccounts);
          this.shopifySyncProducts = true;
        }
      },
      (error) => {
        this.ngxSpinnerService.hide();
        console.error('Error fetching Shopify account details:', error);
      }
    );
  }

  /**
   * Disconnect Shopify account with confirmation popup
   */
  public onDisconnectShopifyAccount(shopifyAccount: ShopifyStoreAccount): void {
    this.confirmationService.confirm({
      message: this.transloco.translate('shopifyAccount.disconnectConfirmMessage'),
      header: this.transloco.translate('shopifyAccount.confirmDisconnectionHeader'),
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.ngxSpinnerService.show();
        this.shopifyService.disconnectShopifyAccount(shopifyAccount.Id).subscribe(
          (response) => {
            this.ngxSpinnerService.hide();

            if (response.Success) {
              this.getShopifyAccountDetails();

              // Reset filter and reload Shopify platform products
              if (this.syncProductsComponent) {
                this.syncProductsComponent.selectedStoreFilter = null;
                this.syncProductsComponent.reloadPlatformProducts('Shopify');
              }

              this.notificationsService.showNotification(
                this.transloco.translate('shopifyAccount.disconnectedTitle'),
                this.transloco.translate('shopifyAccount.disconnectSuccess'),
                NotificationTypes.Success
              );
            } else {
              this.notificationsService.showNotification(
                this.transloco.translate('shopifyAccount.disconnectFailedTitle'),
                response.ResponseMessage || this.transloco.translate('shopifyAccount.disconnectFailed'),
                NotificationTypes.Error
              );
            }
          },
          (error) => {
            this.ngxSpinnerService.hide();
            console.error('Failed to disconnect Shopify account:', error);
            this.notificationsService.showNotification(
              this.transloco.translate('shopifyAccount.errorTitle'),
              this.transloco.translate('shopifyAccount.disconnectError'),
              NotificationTypes.Error
            );
          }
        );
      },
      reject: () => {
        console.log('Shopify account disconnection was cancelled.');
      }
    });
  }

  /**
   * Close Shopify checkout success dialog
   */
  public onCloseShopifyCheckoutDialog() {
    this.isShopifyCheckoutSuccessDialogVisible = false;
  }
}
